import { useState } from "react";
import Image from "@Components/Image";
import Portal from "@Components/Portal";
import BottomSheetOverlay from "@Components/BottomSheetOverlay";
import { FloorPlanRoomType } from "../../utils/floorPlanHelpers";
import styles from "./RoomTypeDropdown.module.scss";
import CloseIcon from "@Icons/CloseIcon";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";

export interface RoomTypeDropdownProps {
  roomTypes: FloorPlanRoomType[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

const RoomTypeDropdown = ({ roomTypes, selectedId, onSelect }: RoomTypeDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslationHook();

  if (roomTypes.length === 0) {
    return null;
  }

  const selectedRoomType = roomTypes.find((rt) => rt.id === selectedId) || roomTypes[0];

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSelect = (id: string) => {
    onSelect(id);
    setIsOpen(false);
  };

  return (
    <>
      <div className={styles.container}>
        <span className={styles.label}>Room Type</span>
        <button
          type="button"
          className={styles.trigger}
          onClick={handleOpen}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-label={`Select room type, currently ${selectedRoomType?.name || "none"}`}
        >
          <span className={styles.triggerText}>{selectedRoomType?.name || "Select"}</span>
          <svg
            className={styles.chevronIcon}
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M4 6L8 10L12 6"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {isOpen && (
        <Portal rootId="forPortal">
          <BottomSheetOverlay
            hideBottomSheetOverlay={handleClose}
            size="fitContent"
            isHistoryPush={false}
            name="RoomTypeDropdownSheet"
          >
            <div className={styles.sheetContent}>
              <div className={styles.sheetHeader}>
                <span className={styles.sheetTitle}>{t("Select Room Type")}</span>
                <button
                  type="button"
                  className={styles.sheetClose}
                  onClick={handleClose}
                  aria-label="Close room type selection"
                >
                  <CloseIcon size={16} stroke="#111928" strokeWidth="2" />
                </button>
              </div>
              <ul className={styles.sheetList} role="listbox" aria-label="Room types">
                {roomTypes.map((roomType) => {
                  const isSelected = roomType.id === selectedId;
                  const thumbnailSrc = roomType.images[0]?.path || roomType.images[0]?.base_path;
                  return (
                    <li
                      key={roomType.id}
                      role="option"
                      aria-selected={isSelected}
                      className={`${styles.sheetItem} ${isSelected ? styles.sheetItemActive : ""}`}
                      onClick={() => handleSelect(roomType.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleSelect(roomType.id);
                        }
                      }}
                      tabIndex={0}
                    >
                      <div className={styles.sheetItemThumbnail}>
                        {thumbnailSrc ? (
                          <Image
                            src={thumbnailSrc}
                            alt={roomType.name}
                            width="100%"
                            height="100%"
                            className=""
                          />
                        ) : (
                          <div className={styles.sheetItemPlaceholder} aria-hidden="true">
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                              <rect
                                x="2"
                                y="4"
                                width="16"
                                height="12"
                                rx="2"
                                stroke="currentColor"
                                strokeWidth="1.5"
                              />
                              <path
                                d="M2 13L6 9L10 13L14 9L18 13"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className={styles.sheetItemInfo}>
                        <span
                          className={`${styles.sheetItemName} ${
                            isSelected ? styles.sheetItemNameActive : ""
                          }`}
                        >
                          {roomType.name}
                        </span>
                        <span className={styles.sheetItemDetails}>
                          {roomType.area && `Area: ${roomType.area}`}
                          {roomType.area && roomType.floor && " • "}
                          {roomType.floor && `Floor ${roomType.floor}`}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </BottomSheetOverlay>
        </Portal>
      )}
    </>
  );
};

export default wrapperHOC(RoomTypeDropdown, {
  componentName: "RoomTypeDropdown-FloorPlansSection-PropertyPage",
  showForChina: true,
});
